import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Span, Link, CheckboxGroup, Checkbox, Text, Field, createListCollection, Box, Portal, Select, Heading, HStack, VStack, Stack, Input } from '@chakra-ui/react';
import { LuPencil as LuPen, LuChevronDown as LuDown, LuChevronRight as LuRight } from "react-icons/lu"
import { FiEdit } from 'react-icons/fi';
import axios from 'axios';
import {pageContentGap} from "components/ui/PageContent";


const SampleSelect = ({ onChange = () => {}, noCheckboxes = false  }) => {

  const [organisms, setOrganisms] = useState([]);
  const [organismNames, setOrganismNames] = useState({});
  const [projects, setProjects] = useState([]);
  const [projectNames, setProjectNames] = useState({});
  const [projectSortNames, setProjectSortNames] = useState({});
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({});
  const [samples, setSamples] = useState([]);
  const [selectedSamples, setSelectedSamples] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [debouncedText, setDebouncedText] = useState('');
  const [uncollapsedProjects, setUncollapsedProjects] = useState(new Set());


  const hasActiveFilters = useMemo(() => { 
    return (filters.organisms?.length > 0 || 
            filters.projects?.length > 0 || 
            debouncedText.trim().length > 0);
  }, [filters, debouncedText]);

  // Add debounce effect to searchText
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedText(searchText);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchText]);

  useEffect(() => {
    const fetchCats = async () => {
      try {
          const response = await axios.get('/api/sample_categories');
          setOrganisms(response.data.organisms.map(o => o.id));
          const _projs = response.data.projects.map(p => p.id);
          setProjects(_projs);
          setProjectNames(response.data.projects.reduce((acc, p) => {
            acc[p.id] = p.display_name;
            return acc;
          }, {}));
          setProjectSortNames(response.data.projects.reduce((acc, p) => {
            acc[p.id] = parseInt(p.name.replace("Project ", ""));
            return acc;
          }, {}));
          setOrganismNames(response.data.organisms.reduce((acc, o) => {
            acc[o.id] = o.name;
            return acc;
          }, {}));

          // Default to selecting all projects once they're loaded
          setFilters(prev => ({
            ...prev,
            projects: _projs
          }));
      } catch (err) {
          console.error('Error fetching categories', err);
          setError(err.message);
      }
    };

    fetchCats();
  }, []);

  const org_coll = useMemo(() => {
    console.log(organisms);
    return createListCollection({
      items: organisms ?? [],
      itemToString: (x) => organismNames[x] || x,
      itemToValue: (x) => x,
    })
  }, [organisms]);

  const proj_coll = useMemo(() => {
    console.log(projects);
    return createListCollection({
      items: projects ?? [],
      itemToString: (x) => projectNames[x] || x,
      itemToValue: (x) => x,
    })
  }, [projects]);

  useEffect(() => {
    const fetchFilteredSamples = async () => {
      // Only proceed if we have project data to work with
      if (filters.projects?.length === 0 && projects.length > 0) {
        setSamples([]);
        setSelectedSamples([]);
        onChange?.([]);
        return;
      }

      try {
        const response = await axios.post('/api/filtered_samples', {
          organisms: filters.organisms || [],
          projects: filters.projects || [],
          sample_names: debouncedText ? [debouncedText] : []
        });
        // sort samples by project name:
        setSamples(response.data);
      
        if (!hasActiveFilters) {
          setSelectedSamples([]);
          onChange?.([]);
        } else {
          const newSampleIds = response.data.map(s => s.id.toString());
          setSelectedSamples(newSampleIds);
          onChange?.(newSampleIds);
        }
        
      } catch (err) {
        console.error('Error fetching samples', err);
        setError(err.message);
      }
    };

    // Only fetch samples if we have projects data
    if (projects.length > 0) {
      fetchFilteredSamples();
    }

  }, [filters, debouncedText, projects.length, hasActiveFilters]);

  const handleOrganismChange = useCallback((e) => {
    setFilters(prev => ({
      ...prev,
      organisms: e.items
    }));
  }, []);
  
  const handleProjectChange = useCallback((e) => {
    console.log("handleProjectChange", e);
    setFilters(prev => ({
      ...prev,
      projects: e.items
    }));
  }, []);

  const ProjectGroup = React.memo(({ project, projectSamples, selectedSamples, onProjectChange }) => {
    const projectSampleIds = projectSamples.map(s => s.id.toString());
    const allChecked = projectSampleIds.every(id => 
      selectedSamples.includes(id)
    );
    const isIndeterminate = projectSampleIds.some(id => 
      selectedSamples.includes(id)
    ) && !allChecked;
  
    const handleProjectToggle = (checked) => {
      const projectIds = projectSampleIds;
      // Remove all project sample IDs first
      const withoutProject = selectedSamples.filter(id => !projectIds.includes(id));
      // Then add them back if checked is true
      const newSelection = checked ? [...withoutProject, ...projectIds] : withoutProject;
      onProjectChange(newSelection);
    };
  
    // const handleSampleToggle = (sampleId, checked) => {
    //   const selectionSet = new Set(selectedSamples);
    //   if (checked) {
    //   selectionSet.add(sampleId);
    //   } else {
    //   selectionSet.delete(sampleId);
    //   }
    //   const newSelection = Array.from(selectionSet);
    //   onProjectChange(newSelection);
    // };

    // Use a more efficient way to handle selections
    const handleSampleToggle = useCallback((sampleId, checked) => {
      setSelectedSamples(prev => {
        const selectionSet = new Set(prev);
        if (checked) {
          selectionSet.add(sampleId);
        } else {
          selectionSet.delete(sampleId);
        }
        const newSelection = Array.from(selectionSet);
        onChange?.(newSelection);
        return newSelection;
      });
    }, [onChange]);

    const toggleCollapse = (e) => {
      // Prevent checkbox toggling when clicking on the collapse icon
      e.stopPropagation();
      setUncollapsedProjects(prev => {
        const newSet = new Set(prev);
        if (newSet.has(project)) {
          newSet.delete(project);
        } else {
          newSet.add(project);
        }
        return newSet;
      });
    };

    const handleProjectEdit = (e) => {
      e.stopPropagation();
      
      if (noCheckboxes && localStorage.getItem('is_superuser') === 'true') {
        const oldProjName = projectNames[project] ;
        const newProjectName = window.prompt('Enter new project name:', oldProjName);
        
        if (newProjectName && newProjectName !== oldProjName) {
          axios.post('/api/rename_project', {
            project_id: project,
            new_name: newProjectName
          })
          .then(response => {
            window.alert(`Project renamed successfully from "${oldProjName}" to "${newProjectName}"`);
            // Refresh the page to show updated project names
            // window.location.reload();
            // rerender instead
            setProjectNames(prev => ({
              ...prev,
              [project]: newProjectName
            }));
          })
          .catch(error => {
            console.error('Error renaming project:', error);
            window.alert(`Failed to rename project: ${error.response?.data?.detail || error.message}`);
          });
        }
      }
    };
  
    return (
      <Stack>
        <HStack>
          <Span 
              cursor="pointer" 
              onClick={toggleCollapse}
              fontSize="lg"
              px="0.0em"
              py="0.15em"
            >
              {!uncollapsedProjects.has(project) ? (<LuRight />) : (<LuDown />)}
          </Span>
          <Checkbox.Root
            checked={isIndeterminate ? 'indeterminate' : allChecked}
            onCheckedChange={(e) => handleProjectToggle(e.checked)}
            // _hover={{cursor: noCheckboxes ? 'default' : 'pointer'}}
            _hover={{cursor: 'pointer'}}
          >
            <Checkbox.HiddenInput />
            {!noCheckboxes && (<Checkbox.Control />)}
            <Checkbox.Label fontWeight="bold" onClick={toggleCollapse}>
              <HStack>
                Project {projectNames[project] || project}
                {noCheckboxes && localStorage.getItem('is_superuser') === 'true' && (
                  <Box 
                    onClick={handleProjectEdit} 
                    cursor="pointer" 
                    _hover={{ color: 'black' }}
                  >
                    <HStack>
                    <Span color="brand.solid" >
                        <LuPen strokeWidth="2.5" />
                      </Span>
                      {/* <Text fontSize="xs" color="brand.solid" fontWeight="normal">
                      Edit Name
                      </Text> */}
                    </HStack>
                    </Box>
                )}
              </HStack>
            </Checkbox.Label>
          </Checkbox.Root>
          
        </HStack>

        {uncollapsedProjects.has(project) && (
        <Stack mb={4} ps={14}>
          {projectSamples.map((sample) => (
            <Checkbox.Root 
              key={sample.id}
              value={sample.id.toString()}
              checked={selectedSamples.includes(sample.id.toString())}
              onCheckedChange={(e) => handleSampleToggle(sample.id.toString(), e.checked)}
              _hover={{cursor: noCheckboxes ? 'default' : 'pointer'}}
            >
              <Checkbox.HiddenInput />
              {!noCheckboxes && (<Checkbox.Control />)}
              <Checkbox.Label  w="100%">
                {noCheckboxes ? (<Link href={`/samples/${sample.id}`} w="100%">
                  <Text>{sample.name}</Text>
                  <Text color="gray.600">({sample.organism})</Text>
                </Link>):
                (<HStack   w="100%">
                  <Text>{sample.name}</Text>
                  <Text color="gray.600">({sample.organism})</Text>
                </HStack>)}
              </Checkbox.Label>
            </Checkbox.Root>
          ))}
        </Stack>
        )}
      </Stack>
    );
  });

  if (error) {
      return <div>Error: {error}</div>;
  }

  return (
    <Stack gap={pageContentGap}>
      <HStack>

      <Select.Root collection={proj_coll} size="sm" width="20em" 
        multiple={proj_coll.items.length > 1}
        value={filters.projects}
        onValueChange={handleProjectChange}
        closeOnSelect={proj_coll.items.length <= 1}
      >
        <Select.HiddenSelect />
        <Select.Label>Projects</Select.Label>
        <Select.Control>
          <Select.Trigger>
            <Select.ValueText placeholder="Select projects" />
          </Select.Trigger>
          <Select.IndicatorGroup>
            <Select.ClearTrigger />
            <Select.Indicator />
          </Select.IndicatorGroup>
        </Select.Control>
        <Portal>
          <Select.Positioner>
            <Select.Content>
              {proj_coll.items.map((project) => (
                <Select.Item item={project} key={project}>
                  {projectNames[project] || project}
                  <Select.ItemIndicator />
                </Select.Item>
              ))}
            </Select.Content>
          </Select.Positioner>
        </Portal>
      </Select.Root>

      <Select.Root collection={org_coll} size="sm" width="20em" 
        multiple={org_coll.items.length > 1}
        value={filters.organisms}
        onValueChange={handleOrganismChange}
        closeOnSelect={org_coll.items.length <= 1}
      >
        <Select.HiddenSelect />
        <Select.Label>Organisms</Select.Label>
        <Select.Control>
          <Select.Trigger>
            <Select.ValueText placeholder="Select organisms" />
          </Select.Trigger>
          <Select.IndicatorGroup>
            <Select.ClearTrigger />
            <Select.Indicator />
          </Select.IndicatorGroup>
        </Select.Control>
        <Portal>
          <Select.Positioner>
            <Select.Content>
              {org_coll.items.map((organism) => (
                <Select.Item item={organism} key={organism}>
                  {organismNames[organism] || organism}
                  <Select.ItemIndicator />
                </Select.Item>
              ))}
            </Select.Content>
          </Select.Positioner>
        </Portal>
      </Select.Root>

      </HStack>

      {/* checkbox to select all projects */}
      <Checkbox.Root
        value="select-all-projects"
        checked={filters.projects && filters.projects.length === projects.length && projects.length > 0}
        onCheckedChange={(e) => {
          const newValues = e.checked ? projects.map(p => p) : [];
          proj_coll.items = newValues;
          handleProjectChange(proj_coll);
          console.log("select all projects");
        }}
      >
        <Checkbox.HiddenInput />
        <Checkbox.Control />
        <Checkbox.Label>Select All Projects</Checkbox.Label>
      </Checkbox.Root>

      <Field.Root>
        <Field.Label>
          Search Sample by Name
        </Field.Label>
        <Input size="sm" placeholder='Search...'
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
        /> 
      </Field.Root>

      {/* Sample list */}
      <Box maxHeight="25em" overflowY="auto" borderWidth="1px" p="1em">
        {/* {!hasActiveFilters ? (
          <Text color="gray.500">
            Please select projects or organisms to view samples.
          </Text>
        ) : ( */}
          <Box >

            {/* Select all functionality */}
            {(!noCheckboxes) && (samples.length > 0) && (
              <HStack 
                justify="space-between" 
                pb="0.5em" pe="0.5em" mb="0.5em" borderBottomWidth="1px"
              >
                <Text fontSize="sm" color="gray.600">
                  {samples.length} sample{samples.length !== 1 ? 's' : ''} found
                </Text>
                
                <Checkbox.Root
                  value="select-all"
                  checked={selectedSamples.length === samples.length && samples.length > 0}
                  onCheckedChange={(e) => {
                    const newValues = e.checked ? samples.map(s => s.id.toString()) : [];
                    setSelectedSamples(newValues);
                    console.log("select all");
                    onChange?.(newValues);
                  }}
                >
                  <Checkbox.HiddenInput />
                  <Checkbox.Control />
                  <Checkbox.Label>Select All</Checkbox.Label>
                </Checkbox.Root>
              </HStack>
            )}


            {/* Sample list grouped by project */}
            <CheckboxGroup
              value={selectedSamples}
              // onValueChange={(values) => {
              //   setSelectedSamples(values);
              //   console.log("group val change");
              //   onChange?.(values);
              // }}
            >
              <Stack>
                {Object.entries(
                  samples.reduce((acc, sample) => {
                    if (!acc[sample.project]) acc[sample.project] = [];
                    acc[sample.project].push(sample);
                    return acc;
                  }, {})
                ).sort((a, b) => (projectSortNames[a[0]] || 0) - (projectSortNames[b[0]] || 0)).map(([project, projectSamples]) => (
                  <ProjectGroup
                    key={project}
                    project={project}
                    projectSamples={projectSamples}
                    selectedSamples={selectedSamples}
                    onProjectChange={(newSelection) => {
                      setSelectedSamples(newSelection);
                      console.log("group proj change");
                      onChange?.(newSelection);
                    }}
                  />
                ))}
                {samples.length === 0 && (
                  <Text color="gray.500">No samples match the current filters</Text>
                )}
              </Stack>
            </CheckboxGroup>




          </Box>
        {/* )} */}
      </Box>

      

    </Stack>
  );
};

export default React.memo(SampleSelect);